import { Link, useLocation } from "@tanstack/react-router";
import {
  BarChart3,
  Bell,
  Boxes,
  ClipboardCheck,
  FileText,
  LayoutDashboard,
  Package,
  Percent,
  Plug,
  RotateCcw,
  ShoppingCart,
  Tags,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { useAttentionCounts } from "@/features/orders";
import { hasAdminRole, hasPermission, useAuth, type Permission } from "@/shared/auth";
import { isFeatureEnabled, type FeatureName } from "@/shared/lib/feature-flags";
import { Tooltip } from "@/shared/ui/tooltip";

interface NavItem {
  key: string;
  to: string;
  icon: LucideIcon;
  comingSoon?: boolean;
  /** When set, the item is only shown to users holding this permission. */
  permission?: Permission;
  /**
   * When set, the item is only shown to users whose `roles` (a separate
   * array from `permissions` — see AuthUser) include "admin". Distinct
   * mechanism from `permission`: some admin-only surfaces (e.g. the global
   * audit log) are gated by role rather than by a granular permission
   * string, mirroring the /audit-log route's own guard.
   */
  adminOnly?: boolean;
  /** When set, the item is only shown while this feature flag is enabled. */
  feature?: FeatureName;
  /** When set, the item is hidden while this feature flag is enabled — inverse of `feature`. */
  hiddenByFeature?: FeatureName;
}

interface NavGroup {
  key: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    key: "operations",
    items: [
      { key: "dashboard", to: "/dashboard", icon: LayoutDashboard },
      { key: "orders", to: "/orders", icon: ShoppingCart },
      {
        key: "manual_review",
        to: "/manual-review",
        icon: ClipboardCheck,
        feature: "manual_review",
      },
      { key: "inventory", to: "/inventory", icon: Package },
      { key: "inventory_diff", to: "/inventory-diff", icon: Boxes },
      { key: "inventory_ratio", to: "/inventory-ratio", icon: Percent },
      { key: "notifications", to: "/notifications", icon: Bell, permission: "notifications:read" },
    ],
  },
  {
    key: "fulfillment",
    items: [{ key: "returns", to: "/returns", icon: RotateCcw, hiddenByFeature: "hide_returns" }],
  },
  {
    key: "promotion",
    items: [
      { key: "campaign", to: "/campaign", icon: BarChart3 },
      { key: "promotion", to: "/promotion", icon: BarChart3 },
    ],
  },
  {
    key: "reports",
    items: [
      { key: "promotion_report", to: "/promotion-report", icon: FileText },
      { key: "inbound_report", to: "/inbound-report", icon: FileText },
      { key: "revenue_report", to: "/revenue-report", icon: FileText, feature: "revenue_report" },
      { key: "export_orders", to: "/export-orders", icon: FileText },
    ],
  },
  {
    key: "admin",
    items: [
      { key: "users", to: "/users", icon: Users, permission: "users:read" },
      { key: "audit_log", to: "/audit-log", icon: FileText, adminOnly: true },
      {
        key: "marketplace_connections",
        to: "/settings/marketplace-connections",
        icon: Plug,
        adminOnly: true,
      },
      { key: "sku_brands", to: "/settings/sku-brands", icon: Tags, adminOnly: true },
    ],
  },
];

export function Sidebar({ collapsed = false }: { collapsed?: boolean }): JSX.Element {
  const { t } = useTranslation("nav");
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = hasAdminRole(user);
  const canSeeAttention = hasPermission(user, "orders:read");
  const { data: attentionCounts } = useAttentionCounts({}, { enabled: canSeeAttention });
  const attentionTotal = canSeeAttention ? (attentionCounts?.total ?? 0) : 0;

  return (
    <aside
      className={[
        "fixed left-0 top-[60px] z-20 h-[calc(100vh-60px)] overflow-y-auto border-r border-[var(--premium-border)] p-3 transition-[width] duration-200",
        collapsed ? "w-[64px]" : "w-[236px]",
      ].join(" ")}
      style={{ background: "var(--grad-sidebar)" }}
      id="sidebar-nav"
      data-testid="sidebar"
    >
      <nav className="flex flex-col gap-4">
        {NAV.map((group) => {
          const items = group.items.filter(
            (item) =>
              (!item.permission || hasPermission(user, item.permission)) &&
              (!item.adminOnly || isAdmin) &&
              (!item.feature || isFeatureEnabled(item.feature)) &&
              (!item.hiddenByFeature || !isFeatureEnabled(item.hiddenByFeature)),
          );
          if (items.length === 0) return null;
          return (
            <div key={group.key} className="flex flex-col">
              {!collapsed && (
                <div
                  data-testid={`sidebar-group-${group.key}`}
                  className="px-3 pb-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-subtle)]"
                >
                  {t(`group.${group.key}`)}
                </div>
              )}
              <ul className="flex flex-col">
                {items.map((item) => {
                  const isActive =
                    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                  const Icon = item.icon;
                  const label = t(`item.${item.key}`);
                  // In collapsed mode the link is the Tooltip trigger; expanded mode renders it directly.
                  const link = (
                    <Link
                      to={item.to}
                      data-testid={`sidebar-item-${item.key}`}
                      aria-current={isActive ? "page" : undefined}
                      aria-label={collapsed ? label : undefined}
                      className={[
                        "relative my-0.5 flex h-[38px] items-center rounded-[9px] text-[13.5px] font-semibold transition-colors",
                        collapsed ? "justify-center px-2" : "gap-2.5 px-3",
                        isActive
                          ? "bg-[linear-gradient(135deg,var(--brand-50),#fff)] text-[var(--brand-700)] shadow-[var(--sh-xs)]"
                          : "text-[var(--text-secondary)] hover:bg-white",
                      ].join(" ")}
                    >
                      {isActive && (
                        <span className="absolute -left-3 bottom-2 top-2 w-[3px] rounded-r bg-[var(--brand-500)]" />
                      )}
                      <Icon
                        className="h-[17px] w-[17px] shrink-0"
                        style={{ color: isActive ? "var(--brand-500)" : "var(--text-subtle)" }}
                      />
                      {!collapsed && <span className="truncate">{label}</span>}
                      {!collapsed && item.comingSoon && (
                        <span
                          data-testid={`sidebar-badge-${item.key}`}
                          className="ml-auto rounded-full bg-[var(--surface-sunken)] px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-[var(--text-subtle)]"
                        >
                          {t("badge_soon")}
                        </span>
                      )}
                      {!collapsed && item.key === "orders" && attentionTotal > 0 && (
                        <span
                          data-testid="sidebar-attention-badge"
                          aria-label={t("badge_attention", { n: attentionTotal })}
                          className="ml-auto flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--brand-500)] px-1 text-[9px] font-bold leading-none text-white"
                        >
                          {attentionTotal > 99 ? "99+" : attentionTotal}
                        </span>
                      )}
                    </Link>
                  );
                  return (
                    <li key={item.key}>
                      {collapsed ? (
                        <Tooltip content={label} side="right">
                          {link}
                        </Tooltip>
                      ) : (
                        link
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
